import { BookingWithDetail, EventSessionWithDetail } from "@garther/interfaces";
import { Button, Popup, Table } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Users } from "lucide-react";
import { useState } from "react";

import { AttendeeAnswersDrawer } from "./attendee-answers-drawer";

interface SessionAttendeeSheetProps {
  session: EventSessionWithDetail | null;
  bookings: BookingWithDetail[];
  onClose: () => void;
}

export function SessionAttendeeSheet({ session, bookings, onClose }: SessionAttendeeSheetProps) {
  const { t, formatDate } = useI18n();
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetail | null>(null);

  const sessionBookings = session ? bookings.filter((b) => b.ticketTier.eventSessionId === session.id) : [];

  const sessionLabel = session
    ? formatDate(session.startAt, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "";

  return (
    <>
      <Popup
        className="max-w-6xl"
        isOpen={session !== null}
        onClose={onClose}
        showCloseButton={true}
        closeOnBackdropClick={true}
        closeOnEscape={true}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="pb-4 border-b border-border">
            <p className="text-responsive-lg font-medium text-text-primary">
              {t("eventDetail.attendees.sessionAttendees.title")}
            </p>
            {session && (
              <div className="mt-1 space-y-0.5">
                <p className="text-responsive-sm text-text-secondary">{sessionLabel}</p>
                <p className="text-responsive-sm text-text-secondary">
                  {t("eventDetail.attendees.sessionAttendees.subtitle", {
                    count: sessionBookings.length
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto py-4">
            {sessionBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-4">
                <div className="bg-background-highlight p-5 rounded-full">
                  <Users className="w-10 h-10 text-text-secondary" strokeWidth={1.5} />
                </div>
                <div className="space-y-1">
                  <p className="text-responsive-base font-medium text-text-primary">
                    {t("eventDetail.attendees.empty.title")}
                  </p>
                  <p className="text-responsive-sm text-text-secondary">
                    {t("eventDetail.attendees.empty.description")}
                  </p>
                </div>
              </div>
            ) : (
              <Table
                data={sessionBookings}
                columns={[
                  {
                    key: "name",
                    title: t("common.name"),
                    dataIndex: "user.name"
                  },
                  {
                    key: "email",
                    title: t("common.email"),
                    dataIndex: "user.email"
                  },
                  {
                    key: "ticket",
                    title: "Ticket Tier",
                    dataIndex: "ticketTier.title"
                  },
                  {
                    key: "quantity",
                    title: "Qty",
                    dataIndex: "quantity"
                  },
                  {
                    key: "answers",
                    title: t("eventDetail.attendees.actions.title"),
                    render: (_, record: BookingWithDetail) => (
                      <Button variant="ghost" padding="px-0" onClick={() => setSelectedBooking(record)}>
                        {t("eventDetail.attendees.actions.viewAnswers")}
                      </Button>
                    )
                  }
                ]}
              />
            )}
          </div>
        </div>
      </Popup>

      <AttendeeAnswersDrawer booking={selectedBooking} onClose={() => setSelectedBooking(null)} />
    </>
  );
}
